import {
  ATTR_SERVICE_NAME,
  ATTR_TELEMETRY_SDK_NAME,
  ATTR_TELEMETRY_SDK_LANGUAGE,
  ATTR_TELEMETRY_SDK_VERSION
} from '@opentelemetry/semantic-conventions';

import {BatchSpanProcessor, WebTracerProvider} from '@opentelemetry/sdk-trace-web';
import {FetchInstrumentation} from '@opentelemetry/instrumentation-fetch';
import {registerInstrumentations} from '@opentelemetry/instrumentation';
import {OTLPTraceExporter} from '@opentelemetry/exporter-trace-otlp-http';
import {SpanStatusCode, trace} from '@opentelemetry/api';
import {Resource} from '@opentelemetry/resources';
import {ZoneContextManager} from '@opentelemetry/context-zone';
import {UserInteractionInstrumentation} from '@opentelemetry/instrumentation-user-interaction';

if (+__IS_OPEN_TELEMETRY_ENABLED__) {
  console.log('🚀 OpenTelemetry initializing...',
    __OPEN_TELEMETRY_SERVICE_NAME__,
    __ENVIRONMENT__,
    __OPEN_TELEMETRY_TRACE_URL__);

  try {
    const resource = new Resource({
      [ATTR_SERVICE_NAME]: __OPEN_TELEMETRY_SERVICE_NAME__,
      [ATTR_TELEMETRY_SDK_NAME]: 'opentelemetry',
      [ATTR_TELEMETRY_SDK_LANGUAGE]: 'webjs',
      [ATTR_TELEMETRY_SDK_VERSION]: '1.30.1',

      'deployment.environment': __ENVIRONMENT__,
      'browser.user_agent': navigator.userAgent,
      'browser.language': navigator.language,
      'page.original_url': window.location.href,
    });

    const traceExporter = new OTLPTraceExporter({
      url: __OPEN_TELEMETRY_TRACE_URL__,
    });

    const tracerProvider = new WebTracerProvider({
      resource: resource,
      spanProcessors: [
        new BatchSpanProcessor(traceExporter, {
          maxQueueSize: 100,
          maxExportBatchSize: 50,
          scheduledDelayMillis: 5000,
          exportTimeoutMillis: 30000,
        }),
      ],
    });

    tracerProvider.register({
      contextManager: new ZoneContextManager(),
    });

    trace.setGlobalTracerProvider(tracerProvider);

    registerInstrumentations({
      instrumentations: [
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: [
            'https://test.admin.adverton.pro/*',
            'https://test.admin.adverton.kz/*',
            'https://stage.admin.adverton.pro/*',
            'https://admin.adverton.pro/*',
            'https://admin.adverton.kz/*',
            'http://localhost:3013/*'],

          applyCustomAttributesOnSpan: function (span, request, result) {
            span.setAttribute('page.pathname', window.location.pathname);
            span.setAttribute('page.href', window.location.href);
            span.setAttribute('page.search', window.location.search);
            span.setAttribute('page.hash', window.location.hash);

            if (request) {
              span.setAttribute('http.url', request.url);
              span.setAttribute('http.method', request.method || 'GET');

              const safeHeaders = {};
              request.headers.forEach((value, key) => {
                if (!['authorization', 'cookie', 'x-api-key'].includes(key.toLowerCase())) {
                  safeHeaders[key] = value;
                }
              });
              span.setAttribute('http.request.headers', JSON.stringify(safeHeaders));

            }

            if (result) {
              span.setAttribute('http.status_code', result.status);
              span.setAttribute('http.status_text', result.statusText);

              const responseHeaders = {};
              result.headers.forEach((value, key) => {
                responseHeaders[key] = value;
              });
              span.setAttribute('http.response.headers', JSON.stringify(responseHeaders));

              const contentLength = result.headers.get('content-length');
              if (contentLength) {
                span.setAttribute('http.response.size', parseInt(contentLength));
              }

              const contentType = result.headers.get('content-type');
              if (contentType) {
                span.setAttribute('http.response.content_type', contentType);
              }
            }
          },

          onFetchError: function (span, request, error) {
            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: error.message
            });
            span.setAttribute('http.error.message', error.message);
            span.setAttribute('http.error.stack', error.stack);

            if (error.name) {
              span.setAttribute('http.error.name', error.name);
            }
          },

          ignoreMethods: ['OPTIONS', 'HEAD'],
        }),

        new UserInteractionInstrumentation({
          eventNames: ['click', 'submit', 'input', 'change'],
        }),
      ],
    });

  } catch (error) {
    console.error('%c❌ OpenTelemetry initialization FAILED:', 'color: red; font-size: 16px');
    console.error(error);
  }
}